import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, Lock, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { LOGO_RAIZES_WHITE } from '../lib/branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    login: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.login || !formData.password) {
      toast.error('Preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, formData);
      
      localStorage.setItem('admin_token', res.data.token);
      localStorage.setItem('admin_data', JSON.stringify({
        admin_id: res.data.admin_id,
        admin_name: res.data.admin_name,
        admin_email: res.data.email,
        role: res.data.role,
        permissions: res.data.permissions || []
      }));
      
      toast.success(`Bem-vindo, ${res.data.admin_name}!`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      toast.error(error.response?.data?.detail || 'Celular/email ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A4D2E] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />
      
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="text-center mb-8">
          <img 
            src={LOGO_RAIZES_WHITE}
            alt="Ministério Raízes"
            data-testid="logo-raizes"
            className="h-20 mx-auto mb-4 drop-shadow-xl"
          />
          <h1 className="font-nunito font-bold text-3xl text-white mb-1">
            Ministerio Raizes
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Shield className="w-4 h-4 text-white/70" />
            <p className="font-inter text-white/70">
              Area Administrativa
            </p>
          </div>
        </div>

        <Card className="bg-white rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="login" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Celular ou Email
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="login"
                  name="login"
                  type="text"
                  data-testid="input-email"
                  value={formData.login}
                  onChange={handleChange}
                  placeholder="(15) 99999-9999 ou email"
                  className="pl-11 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E]"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="input-password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Digite sua senha"
                  className="pl-11 pr-10 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E]"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-password">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-right mt-1">
                <button type="button" onClick={() => navigate('/recuperar-senha?tipo=admin')}
                  className="font-inter text-xs text-[#1A4D2E] hover:underline" data-testid="forgot-password-link">
                  Esqueci minha senha
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </Card>

        <div className="text-center mt-6 space-y-3">
          <button
            onClick={() => navigate('/app-pais/login')}
            className="font-nunito font-bold text-[#F4D35E] hover:text-[#f5dc77] text-sm tracking-wider block mx-auto bg-[#1A4D2E]/50 px-6 py-2 rounded-full"
          >
            Portal da Família
          </button>
          <button
            onClick={() => navigate('/')}
            className="font-inter text-white/50 hover:text-white/80 text-sm underline block mx-auto"
          >
            Voltar ao início
          </button>
        </div>

        <div className="flex items-center justify-center gap-6 mt-10 mb-6">
          <img src="/logos/logo_igreja_familia.png" alt="Igreja Família" className="h-12 opacity-80" />
          <img src="/logos/logo_clubinho.png" alt="Clubinho" className="h-12 opacity-80" />
        </div>
      </div>
    </div>
  );
};

export default LoginAdmin;
